import { useState } from 'react';

interface Service {
  id: string;
  icon: string;
  label: string;
  desc: string;
  color: string;
  glow: string;
}

const services: Service[] = [
  // Level 1 - Top
  {
    id: 'motorsport',
    icon: 'ri-speed-line',
    label: 'Motorsport',
    desc: 'Motorhome, moto da competizione, auto senza conducente e attrezzature racing',
    color: 'border-red-500 bg-red-950/30',
    glow: 'shadow-red-500/40',
  },
  // Level 2
  {
    id: 'eventi',
    icon: 'ri-building-2-line',
    label: 'Eventi Fieristici',
    desc: 'Stand, impianti audio/video e strutture personalizzabili per fiere ed eventi',
    color: 'border-orange-500 bg-orange-950/30',
    glow: 'shadow-orange-500/40',
  },
  {
    id: 'catering',
    icon: 'ri-restaurant-line',
    label: 'Catering & Hospitality',
    desc: 'Attrezzature professionali per eventi catering e hospitality di alto livello',
    color: 'border-amber-500 bg-amber-950/30',
    glow: 'shadow-amber-500/40',
  },
  // Level 3
  {
    id: 'ricambi',
    icon: 'ri-settings-3-line',
    label: 'Ricambistica Auto',
    desc: 'Ricambi di alta qualità per auto da competizione e settore automobilistico',
    color: 'border-red-400 bg-red-950/20',
    glow: 'shadow-red-400/30',
  },
  {
    id: 'energia',
    icon: 'ri-flashlight-line',
    label: 'Energia & Generatori',
    desc: 'Generatori e soluzioni per eventi sostenibili ed efficienti energeticamente',
    color: 'border-yellow-500 bg-yellow-950/20',
    glow: 'shadow-yellow-500/30',
  },
  {
    id: 'camper',
    icon: 'ri-car-line',
    label: 'Camper & Veicoli',
    desc: 'Noleggio camper, motorhome e veicoli specializzati per eventi e competizioni',
    color: 'border-orange-400 bg-orange-950/20',
    glow: 'shadow-orange-400/30',
  },
];

const PyramidIcon = ({ service, size }: { service: Service; size: 'lg' | 'md' | 'sm' }) => {
  const [hovered, setHovered] = useState(false);

  const sizeClasses = {
    lg: 'w-36 h-36 md:w-44 md:h-44',
    md: 'w-28 h-28 md:w-36 md:h-36',
    sm: 'w-24 h-24 md:w-28 md:h-28',
  };

  const iconSizes = {
    lg: 'text-4xl md:text-5xl',
    md: 'text-3xl md:text-4xl',
    sm: 'text-2xl md:text-3xl',
  };

  const labelSizes = {
    lg: 'text-sm md:text-base',
    md: 'text-xs md:text-sm',
    sm: 'text-xs',
  };

  return (
    <div
      className="flex flex-col items-center gap-3 cursor-pointer group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`
          relative ${sizeClasses[size]} rounded-full border-2 ${service.color}
          flex items-center justify-center
          transition-all duration-500
          ${hovered ? `scale-110 shadow-2xl ${service.glow}` : 'scale-100'}
        `}
      >
        {/* Rotating ring */}
        <div
          className={`absolute inset-0 rounded-full border border-white/10 transition-all duration-700 ${
            hovered ? 'rotate-180 border-white/20' : ''
          }`}
          style={{ transform: hovered ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
        {/* Pulse ring */}
        {hovered && (
          <div className={`absolute inset-0 rounded-full border-2 ${service.color} animate-ping opacity-30`} />
        )}
        <div className={`w-full h-full flex items-center justify-center`}>
          <i className={`${service.icon} ${iconSizes[size]} text-white`} />
        </div>
      </div>

      <div className="text-center max-w-[140px]">
        <span
          className={`font-['Exo_2'] font-bold text-white tracking-widest uppercase ${labelSizes[size]} block`}
        >
          {service.label}
        </span>
        {hovered && (
          <p className="font-['Inter'] text-xs text-gray-400 mt-1 leading-relaxed transition-all duration-300">
            {service.desc}
          </p>
        )}
      </div>
    </div>
  );
};

const PyramidServices = () => {
  const level1 = services.slice(0, 1);
  const level2 = services.slice(1, 3);
  const level3 = services.slice(3, 6);

  return (
    <section id="servizi" className="relative bg-[#0a0a10] py-24 overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#08080c] via-transparent to-[#08080c]" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(ellipse at 50% 50%, rgba(220,38,38,0.08) 0%, transparent 70%)`,
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-red-800/40 rounded-full mb-6 bg-red-950/10">
            <span className="font-['Exo_2'] text-xs text-red-400 tracking-[0.3em] uppercase">I Nostri Servizi</span>
          </div>
          <h2 className="font-['Exo_2'] font-black text-white text-4xl md:text-5xl tracking-tight mb-4">
            SOLUZIONI <span className="text-red-500">COMPLETE</span>
          </h2>
          <p className="font-['Inter'] text-gray-500 text-base max-w-xl mx-auto">
            Dalla pista alle fiere, dal catering all&apos;energia: tutto ciò di cui hai bisogno, in un unico partner.
          </p>
        </div>

        {/* Pyramid Layout */}
        <div className="flex flex-col items-center gap-12 md:gap-16">
          {/* Level 1 - Top */}
          <div className="flex justify-center">
            {level1.map((s) => (
              <PyramidIcon key={s.id} service={s} size="lg" />
            ))}
          </div>

          {/* Connector lines */}
          <div className="hidden md:block relative w-full max-w-lg -mt-8 -mb-8">
            <svg viewBox="0 0 400 60" className="w-full opacity-30">
              <line x1="200" y1="0" x2="100" y2="60" stroke="#dc2626" strokeWidth="1" />
              <line x1="200" y1="0" x2="300" y2="60" stroke="#dc2626" strokeWidth="1" />
            </svg>
          </div>

          {/* Level 2 */}
          <div className="flex justify-center gap-12 md:gap-24">
            {level2.map((s) => (
              <PyramidIcon key={s.id} service={s} size="md" />
            ))}
          </div>

          {/* Connector lines */}
          <div className="hidden md:block relative w-full max-w-2xl -mt-8 -mb-8">
            <svg viewBox="0 0 600 60" className="w-full opacity-30">
              <line x1="150" y1="0" x2="75" y2="60" stroke="#f97316" strokeWidth="1" />
              <line x1="150" y1="0" x2="300" y2="60" stroke="#f97316" strokeWidth="1" />
              <line x1="450" y1="0" x2="300" y2="60" stroke="#f97316" strokeWidth="1" />
              <line x1="450" y1="0" x2="525" y2="60" stroke="#f97316" strokeWidth="1" />
            </svg>
          </div>

          {/* Level 3 - Base */}
          <div className="flex justify-center gap-6 md:gap-16 flex-wrap">
            {level3.map((s) => (
              <PyramidIcon key={s.id} service={s} size="sm" />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <p className="font-['Inter'] text-gray-500 text-sm mb-6">
            Hai bisogno di una soluzione personalizzata?
          </p>
          <a
            href="#contatti"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contatti')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-3 border border-red-700/50 rounded-full text-red-400 hover:text-white hover:border-red-500 hover:bg-red-950/30 font-['Exo_2'] text-sm tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
            Richiedi un Preventivo
          </a>
        </div>
      </div>
    </section>
  );
};

export default PyramidServices;
